import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Building2, Plus, CheckCircle, XCircle, Users2, ClipboardList } from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'

const STATUS_COLORS: Record<string, string> = {
  pending:  'bg-warning/15 text-warning',
  approved: 'bg-success/15 text-success',
  rejected: 'bg-destructive/15 text-destructive',
  proposed: 'bg-primary/10 text-primary',
}

export function PortalInstitutionView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [proposing, setProposing] = useState(false)
  const [proposeOpen, setProposeOpen] = useState(false)
  const [proposeForm, setProposeForm] = useState({ member_id: '', org_id: '' })

  const { data: profile } = useQuery({
    queryKey: ['journalist-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('journalist_profiles')
        .select('id, account_type')
        .eq('id', user.id)
        .single() as any)
      return data as { id: string; account_type: string } | null
    }
  })

  const { data: members } = useQuery({
    queryKey: ['institution-members', profile?.id],
    enabled: !!profile?.id && profile?.account_type === 'institution',
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_institution_members' as any)
        .select('id, member_id, status, journalist_profiles!member_id(full_name, id), email')
        .eq('institution_id', profile!.id) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: accreditations } = useQuery({
    queryKey: ['institution-accreditations', profile?.id],
    enabled: !!profile?.id && profile?.account_type === 'institution',
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_accreditations' as any)
        .select('org_id, organizations(name)')
        .eq('journalist_id', profile!.id)
        .eq('status', 'active') as any)
      return (data ?? []) as any[]
    }
  })

  const { data: assignments } = useQuery({
    queryKey: ['institution-assignments', profile?.id],
    enabled: !!profile?.id && profile?.account_type === 'institution',
    queryFn: async () => {
      const { data } = await (supabase
        .from('journalist_institution_org_assignments' as any)
        .select(`
          id, status, proposed_at,
          journalist_profiles!member_id(full_name),
          organizations(name)
        `)
        .eq('institution_id', profile!.id)
        .order('proposed_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    }
  })

  const inviteMember = async () => {
    if (!inviteEmail || !profile?.id) return
    setInviting(true)
    try {
      await supabase.functions.invoke('invite-journalist', {
        body: { email: inviteEmail, institution_id: profile.id }
      })
      toast({ title: 'Invitation sent.' })
      setInviteEmail('')
      setInviteOpen(false)
      qc.invalidateQueries({ queryKey: ['institution-members', profile.id] })
    } catch {
      toast({ title: 'Failed to send invitation.', variant: 'destructive' })
    } finally {
      setInviting(false)
    }
  }

  const updateMemberStatus = async (memberId: string, status: 'approved' | 'rejected') => {
    if (!profile?.id) return
    try {
      await (supabase
        .from('journalist_institution_members' as any)
        .update({ status })
        .eq('id', memberId)
        .eq('institution_id', profile.id) as any)
      toast({ title: `Member ${status}.` })
      qc.invalidateQueries({ queryKey: ['institution-members', profile.id] })
    } catch {
      toast({ title: 'Failed to update member.', variant: 'destructive' })
    }
  }

  const proposeAssignment = async () => {
    if (!proposeForm.member_id || !proposeForm.org_id || !profile?.id) return
    setProposing(true)
    try {
      await (supabase
        .from('journalist_institution_org_assignments' as any)
        .insert({
          institution_id: profile.id,
          member_id: proposeForm.member_id,
          org_id: proposeForm.org_id,
          status: 'proposed',
          proposed_at: new Date().toISOString(),
        }) as any)
      toast({ title: 'Assignment proposed.' })
      setProposeForm({ member_id: '', org_id: '' })
      setProposeOpen(false)
      qc.invalidateQueries({ queryKey: ['institution-assignments', profile.id] })
    } catch {
      toast({ title: 'Failed to propose assignment.', variant: 'destructive' })
    } finally {
      setProposing(false)
    }
  }

  const approveAssignment = async (assignmentId: string, memberId: string, orgId: string) => {
    if (!profile?.id) return
    try {
      await (supabase
        .from('journalist_institution_org_assignments' as any)
        .update({ status: 'approved' })
        .eq('id', assignmentId) as any)
      // Insert journalist_accreditation for member
      await (supabase
        .from('journalist_accreditations' as any)
        .upsert({ journalist_id: memberId, org_id: orgId, status: 'active', accredited_at: new Date().toISOString() }) as any)
      toast({ title: 'Assignment approved.' })
      qc.invalidateQueries({ queryKey: ['institution-assignments', profile.id] })
    } catch {
      toast({ title: 'Failed to approve assignment.', variant: 'destructive' })
    }
  }

  if (profile?.account_type !== 'institution') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-3">
        <Building2 className="w-10 h-10 opacity-30" />
        <p className="text-justify text-sm">This section is only available for institution accounts.</p>
      </div>
    )
  }

  const approvedMembers = (members ?? []).filter((m: any) => m.status === 'approved')

  return (
    <div className="space-y-8">
      <PortalPageHeader
        title="Institution Management"
        description="Manage your team members and organisation assignments."
      />

      {/* My Members */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">My Members</CardTitle>
          <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
            <SheetTrigger asChild>
              <Button size="sm" className="h-7 text-xs gap-1">
                <Plus className="w-3.5 h-3.5" /> Invite Member
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle>Invite a Member</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="journalist@email.com"
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={inviteMember}
                  disabled={inviting || !inviteEmail}
                >
                  {inviting ? 'Sending…' : 'Send Invitation'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </CardHeader>
        <CardContent>
          {(members ?? []).length === 0 ? (
            <PortalEmptyState
              icon={Users2}
              message="No members yet. Invite journalists to join your institution."
              actionLabel="Invite member"
              onAction={() => setInviteOpen(true)}
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Name / Email</th>
                  <th className="pb-2 font-medium text-muted-foreground">Status</th>
                  <th className="pb-2 w-24" />
                </tr>
              </thead>
              <tbody>
                {(members ?? []).map((m: any) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-2">
                      <div className="font-medium">{m.journalist_profiles?.full_name ?? m.email ?? ''}</div>
                    </td>
                    <td className="py-2">
                      <Badge className={`text-[10px] ${STATUS_COLORS[m.status] ?? STATUS_COLORS.pending}`}>
                        {m.status}
                      </Badge>
                    </td>
                    <td className="py-2">
                      {m.status === 'pending' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => updateMemberStatus(m.id, 'approved')}
                            className="p-1 text-success hover:bg-[hsl(var(--success)/0.1)] rounded"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updateMemberStatus(m.id, 'rejected')}
                            className="p-1 text-destructive hover:bg-[hsl(var(--destructive)/0.1)] rounded"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Org Assignments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Organisation Assignments</CardTitle>
          <Sheet open={proposeOpen} onOpenChange={setProposeOpen}>
            <SheetTrigger asChild>
              <Button size="sm" className="h-7 text-xs gap-1" disabled={!approvedMembers.length || !(accreditations ?? []).length}>
                <Plus className="w-3.5 h-3.5" /> Propose Assignment
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle>Propose Assignment</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label>Member</Label>
                  <Select value={proposeForm.member_id} onValueChange={v => setProposeForm(f => ({ ...f, member_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      {approvedMembers.map((m: any) => (
                        <SelectItem key={m.member_id} value={m.member_id}>
                          {m.journalist_profiles?.full_name ?? m.email ?? m.member_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Organisation</Label>
                  <Select value={proposeForm.org_id} onValueChange={v => setProposeForm(f => ({ ...f, org_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {(accreditations ?? []).map((acc: any) => (
                        <SelectItem key={acc.org_id} value={acc.org_id}>
                          {acc.organizations?.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="w-full"
                  onClick={proposeAssignment}
                  disabled={proposing || !proposeForm.member_id || !proposeForm.org_id}
                >
                  {proposing ? 'Submitting…' : 'Submit Proposal'}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </CardHeader>
        <CardContent>
          {(assignments ?? []).length === 0 ? (
            <PortalEmptyState icon={ClipboardList} message="No organisation assignments yet." />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Member</th>
                  <th className="pb-2 font-medium text-muted-foreground">Organisation</th>
                  <th className="pb-2 font-medium text-muted-foreground">Status</th>
                  <th className="pb-2 w-20" />
                </tr>
              </thead>
              <tbody>
                {(assignments ?? []).map((a: any) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 font-medium">{a.journalist_profiles?.full_name ?? ''}</td>
                    <td className="py-2 text-muted-foreground">{a.organizations?.name ?? ''}</td>
                    <td className="py-2">
                      <Badge className={`text-[10px] ${STATUS_COLORS[a.status] ?? STATUS_COLORS.proposed}`}>
                        {a.status}
                      </Badge>
                    </td>
                    <td className="py-2">
                      {a.status === 'proposed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => approveAssignment(a.id, a.member_id, a.org_id)}
                        >
                          Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
